import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/shared/providers/ThemeProvider';
import { Tooltip } from '@/shared/components/ui/tooltip';

interface ThemeSwitchProps {
  /**
   * Compact mode - shows only icon without text
   * Used when sidebar is collapsed
   */
  compact?: boolean;
}

/**
 * Theme Switch
 * Simple toggle between light and dark mode
 * Displays in the sidebar user profile section
 * Supports compact mode for collapsed sidebar
 */
export function ThemeSwitch({ compact = false }: ThemeSwitchProps) {
  const { resolvedTheme, setTheme, isSyncing } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const handleToggle = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  // Compact mode - Icon only with tooltip
  if (compact) {
    return (
      <div className="flex justify-center">
        <Tooltip content={isDark ? 'Modo oscuro activo' : 'Modo claro activo'} side="right">
          <button
            onClick={handleToggle}
            disabled={isSyncing}
            className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2 disabled:opacity-50 hover:scale-105 active:scale-95 group relative overflow-hidden"
            aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {/* Background shine effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />

            {isDark ? (
              <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300 relative z-10" />
            ) : (
              <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300 relative z-10" />
            )}

            {isSyncing && (
              <div className="absolute top-0 right-0 w-2 h-2 rounded-full bg-[var(--brand-primary)] animate-pulse" />
            )}
          </button>
        </Tooltip>
      </div>
    );
  }

  // Full mode - Toggle with text
  return (
    <button
      onClick={handleToggle}
      disabled={isSyncing}
      className="flex items-center gap-2 px-3 py-2 w-full rounded-xl bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2 disabled:opacity-50"
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
    >
      <div className="relative w-10 h-5 rounded-full bg-gray-200 dark:bg-gray-600 transition-colors">
        <div
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white dark:bg-gray-900 shadow-sm transition-transform ${
            isDark ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </div>
      <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
        {isDark ? (
          <>
            <Moon className="w-4 h-4" />
            <span>Oscuro</span>
          </>
        ) : (
          <>
            <Sun className="w-4 h-4" />
            <span>Claro</span>
          </>
        )}
      </div>
      {isSyncing && (
        <div className="ml-auto">
          <div className="w-3 h-3 rounded-full border-2 border-gray-300 border-t-[var(--brand-primary)] animate-spin" />
        </div>
      )}
    </button>
  );
}
